/**
 * Tags as a reader sees them and as a person picks them (FR-14, D17).
 *
 * A tag is an organisation's label with a name and a colour from the kit's
 * tones — painted by `Badge`, never a hex of its own. A document carries tags
 * **by id**, so a rename or a recolour on the Tags screen shows everywhere at
 * once without touching a document.
 *
 * `TagPicker` is the kit's `MultiSelect` over the organisation's tags. It
 * creates a tag **on the fly** only for whoever holds `can_create` on `tag`
 * (the uploader and the reviewer do, plan §5.5): for them a name the list does
 * not have is offered as a new tag, made when the change is saved; for anyone
 * else the list is closed. A typed name that matches an existing tag in any
 * case is that tag — names are unique whatever their case.
 */
import { Alert, Badge, Button, MultiSelect, Skeleton, useToast, type BadgeTone } from '@fiveox/ui'
import { Tag as TagIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { MAX_TAG_NAME, TAG, TAG_COLORS, type DocumentTagsInput, type ProDocClient, type ProDocDocument, type Tag, type TagColor, type Verb } from './prodoc-client'
import { errorText, useLoad } from './hooks'

/** A tag's colour as a `Badge` tone; anything the kit does not name reads as neutral. */
export function tagTone(color: string | null | undefined): BadgeTone {
  return TAG_COLORS.includes(color as TagColor) ? (color as TagColor) : 'neutral'
}

export const TAG_COLOR_LABEL: Record<TagColor, string> = {
  neutral: 'Grey',
  info: 'Blue',
  success: 'Green',
  warning: 'Amber',
  error: 'Red',
}

/** One tag as a pill. */
export function TagBadge({ tag, size = 'sm' }: { tag: Pick<Tag, 'name' | 'color'>; size?: 'sm' | 'md' }) {
  return (
    <Badge tone={tagTone(tag.color)} size={size}>
      <TagIcon size={10} aria-hidden />
      {tag.name}
    </Badge>
  )
}

/** A document's tags by id, named from the organisation's list. `max` folds the rest into "+N". An id the list does not hold (a tag deleted since) is left out. */
export function TagChips({ ids, tags, max = 3, empty = '—' }: { ids: string[] | undefined; tags: Tag[]; max?: number; empty?: string }) {
  const byId = new Map(tags.map((t) => [t.id, t]))
  const found = (ids ?? []).map((id) => byId.get(id)).filter((t): t is Tag => Boolean(t))
  if (found.length === 0) return <span className="text-muted-foreground text-xs">{empty}</span>
  const shown = found.slice(0, max)
  return (
    <span className="flex flex-wrap gap-1">
      {shown.map((t) => (
        <TagBadge key={t.id} tag={t} />
      ))}
      {found.length > shown.length && <Badge size="sm">+{found.length - shown.length}</Badge>}
    </span>
  )
}

/** The organisation's tags, read only for someone who may see them. */
export function useTags(client: ProDocClient, can: (resource: string, verb: Verb) => boolean, accessLoading = false) {
  const visible = can(TAG, 'can_view')
  const loaded = useLoad(() => (visible ? client.tags.list() : Promise.resolve([] as Tag[])), [client, visible])
  return { ...loaded, loading: loaded.loading || accessLoading, list: loaded.data ?? [], visible }
}

/** What a picker holds: tags by id, and new names to make. */
export interface TagSelection {
  tag_ids: string[]
  new_names: string[]
}

/** Split what the picker holds into existing tags (a typed name matching one, in any case, is that tag) and new names. */
export function splitTagValues(values: string[], tags: Tag[]): TagSelection {
  const byId = new Map(tags.map((t) => [t.id, t]))
  const byName = new Map(tags.map((t) => [t.name.trim().toLowerCase(), t]))
  const tag_ids: string[] = []
  const new_names: string[] = []
  for (const raw of values) {
    const known = byId.get(raw) ?? byName.get(raw.trim().toLowerCase())
    if (known) {
      if (!tag_ids.includes(known.id)) tag_ids.push(known.id)
      continue
    }
    const name = raw.trim()
    if (name && !new_names.some((n) => n.toLowerCase() === name.toLowerCase())) new_names.push(name)
  }
  return { tag_ids, new_names }
}

export function TagPicker({
  tags,
  value,
  onChange,
  canCreate,
  label = 'Tags',
  hint,
  size,
  disabled,
}: {
  tags: Tag[]
  value: TagSelection
  onChange: (next: TagSelection) => void
  canCreate: boolean
  label?: string
  hint?: string
  size?: 'sm' | 'md'
  disabled?: boolean
}) {
  const options = useMemo(() => tags.map((t) => ({ value: t.id, label: t.name, sublabel: typeof t.document_count === 'number' ? `${t.document_count}` : undefined })), [tags])
  const tooLong = value.new_names.find((n) => n.length > MAX_TAG_NAME)
  return (
    <MultiSelect
      label={label}
      hint={hint ?? (canCreate ? 'Pick a tag, or type a new name to make one.' : 'Pick from this organisation’s tags.')}
      options={options}
      value={[...value.tag_ids, ...value.new_names]}
      onChange={(next) => onChange(splitTagValues(next, tags))}
      allowUnlisted={canCreate}
      unlistedWarning={(names) => (names.length === 1 ? `“${names[0]}” is a new tag — made when you save.` : `${names.map((n) => `“${n}”`).join(', ')} are new tags — made when you save.`)}
      error={tooLong ? `A tag name is up to ${MAX_TAG_NAME} characters.` : undefined}
      emptyMessage={canCreate ? 'No tag by that name — press Enter to make it.' : 'No tag by that name.'}
      placeholder={canCreate ? 'Find or make a tag…' : 'Find a tag…'}
      size={size}
      disabled={disabled}
    />
  )
}

/**
 * A document's tags on its detail: the pills, and — for whoever may edit the
 * document — the picker and *Save tags* (`PUT /documents/{id}/tags`).
 */
export function DocumentTags({
  client,
  doc,
  tags,
  editable,
  canCreate,
  onSaved,
  onTagsChanged,
}: {
  client: ProDocClient
  doc: ProDocDocument
  tags: ReturnType<typeof useTags>
  editable: boolean
  canCreate: boolean
  onSaved: (next: ProDocDocument) => void
  /** A new tag was made on the way — the list should be read again. */
  onTagsChanged: () => void
}) {
  const { toast } = useToast()
  const current = doc.tag_ids ?? []
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState<TagSelection>({ tag_ids: current, new_names: [] })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    setValue({ tag_ids: doc.tag_ids ?? [], new_names: [] })
  }, [doc.tag_ids])

  if (!tags.visible) return null
  if (tags.loading && !tags.data) return <Skeleton className="h-6 w-40" />

  const changed = value.new_names.length > 0 || value.tag_ids.length !== current.length || value.tag_ids.some((id) => !current.includes(id))
  const save = async () => {
    setBusy(true)
    setError('')
    try {
      const input: DocumentTagsInput = { tag_ids: value.tag_ids, new_tags: value.new_names.map((name) => ({ name })) }
      const next = await client.documents.setTags(doc.id, input)
      if (value.new_names.length) onTagsChanged()
      onSaved(next)
      setEditing(false)
      toast('Tags saved.')
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <TagChips ids={current} tags={tags.list} max={20} empty="No tags" />
        {editable && !editing && (
          <Button variant="link" size="sm" onClick={() => setEditing(true)}>
            {current.length ? 'Edit tags' : 'Add tags'}
          </Button>
        )}
      </div>
      {tags.error && !tags.data && <p className="text-muted-foreground text-xs">The tag names could not be read: {tags.error}</p>}
      {editing && (
        <div className="space-y-3">
          {error && <Alert tone="error">{error}</Alert>}
          <TagPicker tags={tags.list} value={value} onChange={setValue} canCreate={canCreate} size="sm" />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(false)
                setValue({ tag_ids: current, new_names: [] })
                setError('')
              }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={() => void save()} loading={busy} disabled={!changed}>
              Save tags
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
